import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { CompanyId } from '@igf-minar/shared'
import { api, hasAuth } from '../../lib/api'
import { pushRecentCompany } from '../../lib/recent'
import { usePreferences } from '../../lib/preferences'
import { Icon } from './Icon'
import { useMessages } from '@/i18n/messages'
import { shellMessages } from '@/i18n/text/shell'

/** Global "open a customer" search, opened from the topbar or Ctrl/Cmd+K. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const m = useMessages(shellMessages)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const { favorites } = usePreferences()
  const term = q.trim()

  const svrQuery = useQuery({
    queryKey: ['svr2-customers', term],
    queryFn: () => api.admin.svr2Customers.search(term),
    enabled: open && hasAuth() && term.length >= 2,
    staleTime: 60 * 1000,
  })

  const { data } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
    enabled: open && hasAuth(),
    staleTime: 5 * 60 * 1000,
  })

  const results = useMemo(() => {
    const t = term.toLowerCase()
    const companies = (data?.companies ?? []).filter(
      (c) => t || c.source !== 'svr' || favorites.includes(c.id)
    )
    const filtered = t
      ? companies.filter(
          (c) =>
            c.name.toLowerCase().includes(t) ||
            c.shortName.toLowerCase().includes(t) ||
            (c.kennitala ?? '').includes(t)
        )
      : companies
    return filtered.slice(0, 30)
  }, [data, term, favorites])

  const inAppKt = useMemo(
    () => new Set((data?.companies ?? []).map((c) => c.kennitala).filter(Boolean)),
    [data]
  )
  const svrResults = useMemo(
    () => (svrQuery.data?.customers ?? []).filter((c) => !inAppKt.has(c.kennitala)).slice(0, 20),
    [svrQuery.data, inAppKt]
  )

  useEffect(() => {
    if (open) {
      setQ('')
      setActive(0)
      const t = setTimeout(() => inputRef.current?.focus(), 20)
      return () => clearTimeout(t)
    }
  }, [open])

  useEffect(() => {
    setActive(0)
  }, [q])

  if (!open) return null

  async function openByKennitala(kennitala: string, name: string) {
    try {
      const res = await api.admin.svr2Customers.open(kennitala)
      pushRecentCompany({ id: res.companyId, name, shortName: name })
      onClose()
      navigate({ to: '/companies/$id', params: { id: res.companyId as CompanyId }, search: { year: new Date().getFullYear() } })
    } catch { /* ignore */ }
  }

  function openCompany(c: { id: string; name: string; shortName: string }) {
    pushRecentCompany({ id: c.id, name: c.name, shortName: c.shortName })
    onClose()
    navigate({
      to: '/companies/$id',
      params: { id: c.id as CompanyId },
      search: { year: new Date().getFullYear() },
    })
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      onClose()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault()
      openCompany(results[active])
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-24"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={m.searchCustomerLabel}
        className="w-full max-w-xl rounded-2xl bg-[var(--surface)] border border-[var(--border)] shadow-[var(--shadow-lift)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[var(--border)] px-4 text-[var(--faint)]">
          <Icon name="search" className="w-5 h-5" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={m.searchPlaceholder}
            className="flex-1 bg-transparent py-3.5 text-sm text-[var(--fg)] outline-none placeholder:text-[var(--faint)]"
          />
          <kbd className="hidden sm:block font-grotesk rounded-md border border-[var(--border)] px-1.5 py-0.5 text-[10px]">
            Esc
          </kbd>
        </div>

        <ul className="max-h-80 overflow-y-auto py-2">
          {results.length === 0 && svrResults.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-[var(--faint)]">
              {term.length >= 2 ? m.noCustomerFound : m.startTyping}
            </li>
          )}
          {results.map((c, i) => {
            const isLive = c.dataSource === 'live'
            return (
              <li key={c.id}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => openCompany(c)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition"
                  style={i === active ? { background: 'var(--brand-soft)' } : undefined}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-[var(--brand-soft)] font-grotesk text-sm font-bold text-[var(--brand)]">
                      {c.shortName.charAt(0)}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-[var(--fg)]">{c.name}</span>
                      {c.kennitala && (
                        <span className="block font-grotesk text-xs text-[var(--faint)]">kt {c.kennitala}</span>
                      )}
                    </span>
                  </span>
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={
                      isLive
                        ? { background: 'var(--acc-green-soft)', color: 'var(--acc-green)' }
                        : { background: 'var(--surface-2)', color: 'var(--muted)' }
                    }
                  >
                    {isLive ? 'Lifandi' : 'Static'}
                  </span>
                </button>
              </li>
            )
          })}
          {svrResults.length > 0 && (
            <li className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
              {m.svrRegistry}
            </li>
          )}
          {svrResults.map((c) => (
            <li key={c.kennitala}>
              <button
                onClick={() => openByKennitala(c.kennitala, c.name || c.nameAlias || c.kennitala)}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition hover:bg-[var(--surface-2)]"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-[var(--surface-2)] font-grotesk text-sm font-bold text-[var(--muted)]">
                    {(c.name || c.kennitala).charAt(0)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[var(--fg)]">{c.name || c.nameAlias || c.kennitala}</span>
                    <span className="block font-grotesk text-xs text-[var(--faint)]">kt {c.kennitala}</span>
                  </span>
                </span>
                <span className="shrink-0 text-[11px] font-semibold text-[var(--brand)]">{m.fetchAndOpen}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
